// src/components/global/Table/ResourceTableBody.jsx
import React from 'react';
import {
    Box,
    Table,
    TableBody as MuiTableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Checkbox,
} from '@mui/material';
import TablePagination from '../TablePagination';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

// استيراد المكوّنات الجديدة
import LoadingStateRow from './handleStateTable/LoadingStateRow';
import ErrorStateRow from './handleStateTable/ErrorStateRow';
import EmptyStateRow from './handleStateTable/EmptyStateRow';

const TableBody = ({
    isLoading,
    error,
    items,
    meta,
    page,
    perPage,
    onPageChange,
    onPerPageChange,
    sortBy,
    sortDir,
    onRequestSort,
    selectedIds,
    onSelectAllClick,
    onRowCheckboxClick,
    onMenuOpen,
    columns,
    renderRow,
    emptyLabelAr,
    emptyLabelEn,
    loadErrorAr,
    loadErrorEn,
    onRetry,
}) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const { i18n } = useTranslation();
    const lng = i18n.language || 'en';
    const isArabic = lng === 'ar';

    const totalItems = items?.length || 0;
    const colSpan = columns?.length + 2;

    return (
        <Box
            sx={{
                bgcolor: isDark
                    ? theme.palette.background.card
                    : theme.palette.background.section,
            }}
        >
            <TableContainer
                sx={{
                    maxHeight: { xs: 420, sm: 640 },
                    overflow: 'auto',
                    '& td, & th': {
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        verticalAlign: 'middle',
                    },
                }}
            >
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow
                            sx={{
                                backgroundColor: isDark ? '#0B1220' : '#E3F2FD',
                            }}
                        >
                            {onSelectAllClick && (
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        color="primary"
                                        indeterminate={
                                            selectedIds.length > 0 &&
                                            selectedIds.length < totalItems
                                        }
                                        checked={
                                            totalItems > 0 &&
                                            selectedIds.length === totalItems
                                        }
                                        onChange={onSelectAllClick}
                                        inputProps={{ 'aria-label': 'select all' }}
                                    />
                                </TableCell>
                            )}

                            <TableCell
                                sx={{
                                    fontWeight: 700,
                                    width: 80,
                                    color: isDark ? '#E5E7EB' : '#0F172A',
                                }}
                            >
                                {isArabic ? 'م' : 'S/N'}
                            </TableCell>

                            {columns?.map((col) => (
                                <TableCell
                                    key={col.id}
                                    align={col.align || 'left'}
                                    sx={{
                                        fontWeight: 700,
                                        width: col.width,
                                        color: isDark ? '#E5E7EB' : '#0F172A',
                                    }}
                                >
                                    {col.sortable ? (
                                        <TableSortLabel
                                            active={sortBy === col.id}
                                            direction={
                                                sortBy === col.id
                                                    ? (sortDir === "desc" ? "asc" : "desc")
                                                    : "asc"
                                            }
                                            onClick={() => onRequestSort(col.id)}
                                            sx={{
                                                '&.MuiTableSortLabel-root': {
                                                    color: isDark
                                                        ? '#E5E7EB'
                                                        : '#0F172A',
                                                },
                                                '&.MuiTableSortLabel-root:hover': {
                                                    color: theme.palette.primary.main,
                                                },
                                                '&.Mui-active': {
                                                    color: theme.palette.primary.main,
                                                },
                                                '& .MuiTableSortLabel-icon': {
                                                    color: `${theme.palette.primary.main} !important`,
                                                },
                                            }}
                                        >
                                            {isArabic ? col.labelAr : col.labelEn}
                                        </TableSortLabel>
                                    ) : (
                                        isArabic ? col.labelAr : col.labelEn
                                    )}
                                </TableCell>
                            ))}

                            <TableCell
                                align="center"
                                sx={{
                                    fontWeight: 700,
                                    width: 80,
                                    color: isDark ? '#E5E7EB' : '#0F172A',
                                }}
                            >
                                {isArabic ? 'إجراءات' : 'Actions'}
                            </TableCell>
                        </TableRow>
                    </TableHead>

                    <MuiTableBody>
                        {isLoading && (
                            <LoadingStateRow
                                colSpan={colSpan}
                                isArabic={isArabic}
                            />
                        )}

                        {error && !isLoading && (
                            <ErrorStateRow
                                colSpan={colSpan}
                                isArabic={isArabic}
                                messageAr={loadErrorAr}
                                messageEn={loadErrorEn}
                                onRetry={onRetry}
                            />
                        )}

                        {!isLoading && !error && totalItems === 0 && (
                            <EmptyStateRow
                                colSpan={colSpan}
                                isArabic={isArabic}
                                labelAr={emptyLabelAr}
                                labelEn={emptyLabelEn}
                            />
                        )}

                        {!isLoading &&
                            !error &&
                            items.map((item, index) =>
                                renderRow({
                                    key: item.id,
                                    item,
                                    index,
                                    isArabic,
                                    isDark,
                                    page,
                                    perPage,
                                    sortBy,
                                    sortDir,
                                    selectedIds,
                                    onRowCheckboxClick,
                                    onMenuOpen,
                                })
                            )}
                    </MuiTableBody>
                </Table>
            </TableContainer>

            <Box
                sx={{
                    px: 2,
                    py: 1.2,
                    borderTop: (t) => `1px solid ${t.palette.divider}`,
                    bgcolor: isDark ? '#020617' : '#E3F2FD',
                }}
            >
                <TablePagination
                    page={meta?.current_page || page}
                    perPage={meta?.per_page || perPage}
                    total={meta?.total || 0}
                    onPageChange={onPageChange}
                    onPerPageChange={onPerPageChange}
                    perPageOptions={[5, 10, 15, 25, 50, 100]}
                />
            </Box>
        </Box>
    );
};

export default TableBody;
